import PublicShell from "@/components/public/PublicShell";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, RefreshCcw, Wallet, FileText, Users, Building2, CalendarClock } from "lucide-react";

/**
 * VAULT Tools — public utilities. No login. No bank details. No advice.
 * All tools are organisational: they help visitors see how findable and
 * organised their financial life already is.
 */
const TOOLS = [
    { slug: "financial-organisation-checkup", icon: ClipboardCheck, tone: "gold",
      title: "Financial Organisation Checkup",
      teaser: "10 questions. About 2 minutes. Get a VAULT Organisation Score.",
      time: "2 min", live: true },
    { slug: "subscription-audit", icon: RefreshCcw, tone: "coral",
      title: "Subscription Audit",
      teaser: "List every recurring charge in one place — spot the ones you forgot.",
      time: "3 min", live: false },
    { slug: "net-worth-snapshot", icon: Wallet, tone: "emerald",
      title: "Net Worth Snapshot",
      teaser: "The four-block picture: assets, liabilities, and where you actually stand.",
      time: "4 min", live: false },
    { slug: "document-readiness-check", icon: FileText, tone: "teal",
      title: "Financial Document Readiness Check",
      teaser: "Could a family member find the five things they'd need tonight?",
      time: "3 min", live: false },
    { slug: "family-organisation-checklist", icon: Users, tone: "violet",
      title: "Family Financial Organisation Checklist",
      teaser: "The household categories every family should have quietly organised.",
      time: "4 min", live: false },
    { slug: "annual-renewal-planner", icon: CalendarClock, tone: "amber",
      title: "Annual Renewal Planner",
      teaser: "Every annual charge on one calendar you'll actually look at.",
      time: "3 min", live: false },
    { slug: "payment-due-date-planner", icon: CalendarClock, tone: "cobalt",
      title: "Payment Due-Date Planner",
      teaser: "Never miss a payment because the date lived in the wrong place.",
      time: "3 min", live: false },
];

const TONE_STOP = {
    gold: "linear-gradient(135deg,#E6D5A8,#C6A86B)", coral: "linear-gradient(135deg,#FED7AA,#F97316)",
    emerald: "linear-gradient(135deg,#A7F3D0,#10B981)", teal: "linear-gradient(135deg,#99F6E4,#14B8A6)",
    violet: "linear-gradient(135deg,#DDD6FE,#8B5CF6)", amber: "linear-gradient(135deg,#FDE68A,#F59E0B)",
    cobalt: "linear-gradient(135deg,#BFDBFE,#3B82F6)",
};

export default function ToolsHome() {
    const liveTools = TOOLS.filter((t) => t.live);
    const soonTools = TOOLS.filter((t) => !t.live);
    return (
        <PublicShell>
            <div className="th-root" data-testid="tools-home">
                <style>{TH_CSS}</style>
                <section className="th-container th-hero" data-testid="tools-hero">
                    <div className="th-eyebrow">VAULT TOOLS · LIVE</div>
                    <h1 className="th-title">Do something useful in two minutes.</h1>
                    <p className="th-lede">
                        A small public tool that helps you see how organised your financial life already is —
                        before you ever sign up. No CVV. No PIN. No bank details. Nothing stored.
                    </p>
                </section>

                <section className="th-container th-grid" data-testid="tools-grid">
                    {liveTools.map((t) => {
                        const Icon = t.icon;
                        return (
                            <Link key={t.slug} to={`/tools/${t.slug}`} className="th-card th-card--live" data-testid={`tool-${t.slug}`}>
                                <div className="th-card__icon" style={{ background: TONE_STOP[t.tone] }}>
                                    <Icon size={20} strokeWidth={2} color="#0F1E3A"/>
                                </div>
                                <div className="th-card__body">
                                    <div className="th-card__row">
                                        <span className="th-card__time">{t.time}</span>
                                    </div>
                                    <h3 className="th-card__title">{t.title}</h3>
                                    <p className="th-card__teaser">{t.teaser}</p>
                                    <span className="th-card__cta">Start <ArrowRight size={13}/></span>
                                </div>
                            </Link>
                        );
                    })}
                </section>

                {soonTools.length > 0 && (
                    <section className="th-container" data-testid="tools-roadmap" style={{ marginTop: 56 }}>
                        <div className="th-eyebrow" style={{ opacity: 0.72 }}>COMING TO VAULT</div>
                        <h2 className="th-title" style={{ fontSize: "28px", marginTop: 8 }}>More tools on the way.</h2>
                        <p className="th-lede" style={{ marginBottom: 22 }}>
                            We&rsquo;re publishing these next. If any of them would help you today, sign up — we&rsquo;ll
                            let you know the moment each lands.
                        </p>
                        <div className="th-grid">
                            {soonTools.map((t) => {
                                const Icon = t.icon;
                                return (
                                    <div key={t.slug} className="th-card th-card--soon" aria-disabled="true" data-testid={`tool-${t.slug}`}>
                                        <div className="th-card__icon" style={{ background: TONE_STOP[t.tone], opacity: 0.55 }}>
                                            <Icon size={20} strokeWidth={2} color="#0F1E3A"/>
                                        </div>
                                        <div className="th-card__body">
                                            <div className="th-card__row">
                                                <span className="th-card__time">{t.time}</span>
                                                <span className="th-card__soon">SOON</span>
                                            </div>
                                            <h3 className="th-card__title">{t.title}</h3>
                                            <p className="th-card__teaser">{t.teaser}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                <section className="th-container th-cta">
                    <p className="th-cta__note">
                        These tools are organisational, not advice. VAULT never asks for CVVs, PINs, OTPs,
                        or bank passwords.
                    </p>
                    <Link to="/signup" className="th-btn" data-testid="tools-signup">
                        Start Free Trial <ArrowRight size={14}/>
                    </Link>
                </section>
            </div>
        </PublicShell>
    );
}

const TH_CSS = `
.th-root { background: #FBF7EE; color: #0F1E3A; min-height: 100vh; padding-bottom: 96px; }
.th-container { max-width: 1160px; margin: 0 auto; padding: 0 28px; }
.th-hero { padding: 72px 28px 40px; max-width: 780px; }
.th-eyebrow { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 16px; }
.th-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(38px, 6vw, 62px); font-weight: 500; letter-spacing: -0.025em; line-height: 1.02; margin: 0 0 20px; }
.th-lede { font-size: 17px; line-height: 1.6; color: #57534E; max-width: 620px; margin: 0; }
.th-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; padding: 24px 28px 0; }
@media (max-width: 900px) { .th-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 620px) { .th-grid { grid-template-columns: 1fr; } }
.th-card { display: flex; gap: 16px; padding: 22px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 20px; text-decoration: none; color: inherit; transition: transform 220ms cubic-bezier(0.16,1,0.3,1), box-shadow 220ms cubic-bezier(0.16,1,0.3,1), border-color 220ms cubic-bezier(0.16,1,0.3,1); }
.th-card--live:hover { transform: translateY(-4px); box-shadow: 0 24px 48px -20px rgba(15,30,58,0.2); border-color: rgba(198,168,107,0.35); }
.th-card--soon { opacity: 0.72; cursor: default; }
.th-card__icon { width: 44px; height: 44px; border-radius: 14px; display: grid; place-items: center; flex-shrink: 0; box-shadow: 0 8px 20px -8px rgba(15,30,58,0.2); }
.th-card__row { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.th-card__time { font-size: 10px; letter-spacing: 0.24em; font-weight: 700; color: #78716C; }
.th-card__soon { font-size: 9px; letter-spacing: 0.24em; font-weight: 700; color: #C6A86B; padding: 2px 8px; background: rgba(198,168,107,0.14); border-radius: 999px; }
.th-card__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 20px; font-weight: 500; letter-spacing: -0.01em; margin: 0 0 8px; color: #0F1E3A; }
.th-card__teaser { font-size: 13.5px; line-height: 1.5; color: #57534E; margin: 0 0 10px; }
.th-card__cta { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 600; color: #0F1E3A; }
.th-cta { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; padding: 48px 28px 0; }
.th-cta__note { font-size: 12.5px; color: #78716C; max-width: 560px; margin: 0; }
.th-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; border-radius: 999px; background: linear-gradient(180deg,#E6D5A8,#C6A86B); color: #0F1E3A; font-size: 13.5px; font-weight: 600; text-decoration: none; box-shadow: 0 12px 26px -8px rgba(198,168,107,0.55); transition: transform 180ms cubic-bezier(0.16,1,0.3,1); }
.th-btn:hover { transform: translateY(-1px); }
`;
